import { DEFAULT_PIEVE_MODEL_ID, DEFAULT_PIEVE_REASONING_EFFORT } from '@shared/pieve/defaultModel.js';
import getModelReasoningLevels from '@/lib/model/getModelReasoningLevels.js';

const RUN_STATES = new Set(['idle', 'queued', 'generating', 'running_tool', 'awaiting_approval', 'canceling', 'paused', 'blocked', 'needs_desktop', 'retry_available', 'complete', 'error', 'canceled']);

const normalizeThreadComposerModel = (value) => {
  return value;
};

const normalizeThreadComposerReasoningEffort = ({ modelId, reasoning } = {}) => {
  const normalizedModelId = normalizeThreadComposerModel(modelId);
  const normalizedReasoning = getModelReasoningLevels(normalizedModelId).includes(reasoning) ? reasoning : null;

  if (normalizedReasoning) {
    return normalizedReasoning;
  }

  return DEFAULT_PIEVE_REASONING_EFFORT;
};

const normalizeThreadPinned = (thread = {}) => {
  return Boolean(thread?.pinned || thread?.isPinned || thread?.pinnedAt || thread?.pinnedOn);
};

const normalizeThreadPinnedAt = (thread = {}) => {
  const normalized = String(thread?.pinnedAt || thread?.pinnedOn || '').trim();

  if (!normalizeThreadPinned(thread)) {
    return null;
  }

  return normalized || null;
};

const normalizeParentThreadId = (thread = {}) => {
  const normalized = String(thread?.parentThreadId || thread?.parent_thread_id || '').trim();
  return normalized || null;
};

const normalizeProjectName = (value, fallback = 'Project') => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 255);

  if (normalized) {
    return normalized;
  }

  return fallback;
};

const normalizeThreadRunState = (state) => {
  const normalized = String(state || 'idle')
    .trim()
    .toLowerCase();

  if (RUN_STATES.has(normalized)) {
    return normalized;
  }

  if (normalized === 'running') {
    return 'generating';
  }

  return 'idle';
};

const normalizeRunStateField = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const normalizeRunStateIsoField = (value) => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return null;
  }

  const parsed = Date.parse(normalized);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return new Date(parsed).toISOString();
};

const normalizeRunStateDurationField = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
};

const getThreadRunStateBlockers = (run = {}) => {
  const blocker = run?.blocker && typeof run.blocker === 'object' ? run.blocker : {};

  return {
    blockerCode: normalizeRunStateField(run?.blockerCode || run?.blocker_code || blocker?.code),
    blockerReason: normalizeRunStateField(run?.blockerReason || run?.blocker_reason || blocker?.reason),
    blockerMessage: normalizeRunStateField(run?.blockerMessage || run?.blocker_message || blocker?.message)
  };
};

const getThreadRunStateTiming = (run = {}) => {
  return {
    startedAt: normalizeRunStateIsoField(run?.startedAt || run?.started_at),
    completedAt: normalizeRunStateIsoField(run?.completedAt || run?.completed_at),
    durationMs: normalizeRunStateDurationField(run?.durationMs || run?.duration_ms),
    activeDurationMs: normalizeRunStateDurationField(run?.activeDurationMs || run?.active_duration_ms),
    blockedDurationMs: normalizeRunStateDurationField(run?.blockedDurationMs || run?.blocked_duration_ms) ?? 0
  };
};

const normalizeThreadRunStatePayload = ({ run = {}, fallbackRunId = null, fallbackState = null } = {}) => {
  const source = run && typeof run === 'object' ? run : {};
  const normalizedState = normalizeThreadRunState(source?.state || fallbackState || 'idle');
  const runId = source?.runId || source?.id || fallbackRunId || null;
  const blockers = getThreadRunStateBlockers(source);
  const timing = getThreadRunStateTiming(source);

  return {
    state: normalizedState,
    runId,
    blockerCode: blockers.blockerCode,
    blockerReason: blockers.blockerReason,
    blockerMessage: blockers.blockerMessage,
    startedAt: timing.startedAt,
    completedAt: timing.completedAt,
    durationMs: timing.durationMs,
    activeDurationMs: timing.activeDurationMs,
    blockedDurationMs: timing.blockedDurationMs
  };
};

export { DEFAULT_PIEVE_MODEL_ID as DEFAULT_PIEVE_COMPOSER_MODEL, DEFAULT_PIEVE_REASONING_EFFORT as DEFAULT_PIEVE_COMPOSER_REASONING_EFFORT, getThreadRunStateBlockers, getThreadRunStateTiming, normalizeParentThreadId, normalizeProjectName, normalizeRunStateIsoField, normalizeThreadComposerModel, normalizeThreadComposerReasoningEffort, normalizeThreadPinned, normalizeThreadPinnedAt, normalizeThreadRunState, normalizeThreadRunStatePayload };
